import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Pagination
} from '@mui/material';
import {
  People as PeopleIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  PersonAdd as LoginAsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { UsersService, User } from '../../../services/api';
import AdminService from '../../../services/AdminService';
import { ImpersonationService } from '../../../services/ImpersonationService';
import { useNotifications } from '../../../contexts/NotificationContext';

/**
 * All Users Page Component
 * Main user management interface for admins with real API integration
 */
const AllUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useNotifications();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [impersonating, setImpersonating] = useState(false);

  // Load user stats and users list
  useEffect(() => {
    loadUserStats();
    loadUsers();
  }, [currentPage, searchTerm, roleFilter, statusFilter]);

  const loadUserStats = async () => {
    try {
      const adminStats = await AdminService.getStats();
      setStats({
        total: adminStats.total_users,
        active: adminStats.active_users,
        new_this_month: adminStats.recent_registrations,
        online: adminStats.active_today
      });
    } catch (err) {
      console.error('Error loading user stats:', err);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        page: currentPage,
        limit: 10
      };
      
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      if (roleFilter !== 'all') {
        params.role = roleFilter;
      }
      
      if (statusFilter !== 'all') {
        params.status = statusFilter === 'active' ? 'active' : 'inactive';
      }

      const response = await UsersService.getUsers(params);
      setUsers(response.users);
      setTotalUsers(response.total);
      setTotalPages(response.last_page);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await UsersService.deleteUser(userId);
      loadUsers(); // Reload the list
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user. Please try again.');
    }
  };

  const handleEditUser = (userId: number) => {
    navigate(`/dashboard/admin/users/edit/${userId}`);
  };

  const handleLoginAs = async (user: User) => {
    if (!user.is_active) {
      showError('Cannot impersonate inactive users', 'Impersonation Error');
      return;
    }

    if (!window.confirm(`Are you sure you want to login as ${user.name}? You will be redirected to their dashboard.`)) {
      return;
    }

    setImpersonating(true);
    try {
      const response = await ImpersonationService.startImpersonation(user.id);
      
      if (response.success) {
        success(`Now viewing as ${user.name}. Click "Stop Impersonating" in the banner to return.`);
        
        // Reload the page to force re-authentication with impersonation headers
        // The impersonation headers are now stored in localStorage and will be
        // included in all API requests automatically
        // Use relative URL to work in both dev (port 3000) and production (port 80)
        setTimeout(() => {
          window.location.href = response.dashboard_url;
        }, 1000);
      }
    } catch (err: any) {
      console.error('Failed to start impersonation:', err);
      showError(
        err.response?.data?.message || 'Failed to start impersonation. Please try again.',
        'Impersonation Error'
      );
      setImpersonating(false);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'success' : 'error';
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'project_manager': return 'warning';
      case 'developer': return 'info';
      case 'client': return 'default';
      default: return 'default';
    }
  };

  const handleCreateUser = () => {
    navigate('/dashboard/admin/users/create');
  };

  if (loading && users.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            User Management
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateUser}
          >
            Add New User
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* User Statistics Cards */}
        {stats && (
          <Box display="flex" gap={2} sx={{ mb: 4, flexWrap: 'wrap' }}>
            <Card sx={{ flex: '1 1 200px' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Users
                </Typography>
                <Typography variant="h4">
                  {stats.total || totalUsers}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 200px' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Active Users
                </Typography>
                <Typography variant="h4">
                  {stats.active || users.filter(u => u.is_active).length}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 200px' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  New This Month
                </Typography>
                <Typography variant="h4">
                  {stats.new_this_month || 0}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: '1 1 200px' }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Online Now
                </Typography>
                <Typography variant="h4">
                  {stats.online || 0}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
              <Box flex="1 1 250px">
                <TextField
                  fullWidth
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Box>
              <Box flex="1 1 150px">
                <TextField
                  fullWidth
                  select
                  label="Role"
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="project_manager">Project Manager</MenuItem>
                  <MenuItem value="developer">Developer</MenuItem>
                  <MenuItem value="client">Client</MenuItem>
                </TextField>
              </Box>
              <Box flex="1 1 150px">
                <TextField
                  fullWidth
                  select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </TextField>
              </Box>
              <Box flex="0 0 120px">
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={loadUsers}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Search'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Joined</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar sx={{ mr: 2 }}>
                            {user.name?.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="body2" fontWeight="medium">
                            {user.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip 
                          label={user.role?.replace('_', ' ').toUpperCase() || 'USER'} 
                          color={getRoleColor(user.role)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={user.is_active ? 'Active' : 'Inactive'} 
                          color={getStatusColor(user.is_active)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() => handleLoginAs(user)}
                          size="small"
                          color="secondary"
                          disabled={!user.is_active || impersonating}
                          title={!user.is_active ? 'Cannot impersonate inactive users' : 'Login as this user'}
                        >
                          <LoginAsIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleEditUser(user.id)}
                          size="small"
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteUser(user.id)}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}

            {users.length === 0 && !loading && (
              <Box textAlign="center" py={4}>
                <PeopleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="textSecondary">
                  No users found
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {searchTerm || roleFilter !== 'all' || statusFilter !== 'all'
                    ? 'Try adjusting your search criteria'
                    : 'Get started by creating your first user'
                  }
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default AllUsersPage;